import React, { memo, useCallback, useState } from 'react';

import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { ArrowCheckIcon, PinFilledIcon } from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { IconDirection, IconSize } from 'constants/coreDesignSystem';

import { getMessageId, getMessageText } from '../TeamChat.utils';

import {
  PINNED_STRIP_COLLAPSE_THRESHOLD,
  PINNED_STRIP_COLLAPSE_TOOLTIP,
  PINNED_STRIP_EXPAND_TOOLTIP,
  PINNED_STRIP_HEADER_LABEL,
  PINNED_STRIP_UNPIN_TOOLTIP,
} from './TeamChatPinnedStrip.constants';
import {
  headerLabelStyles,
  headerStyles,
  itemBodyStyles,
  itemPreviewStyles,
  itemSenderStyles,
  itemStyles,
  rootStyles,
} from './TeamChatPinnedStrip.styles';
import { TeamChatPinnedStripTestIds } from './TeamChatPinnedStrip.testIds';
import {
  buildPinnedPreview,
  getPinnedSenderLabel,
  onJumpToPinnedMessage,
} from './TeamChatPinnedStrip.utils';

const TeamChatPinnedStrip = memo(({ messages, members = [], pinnedIds, onUnpin }) => {
  const pinnedMessages = messages.filter(message => pinnedIds.includes(getMessageId(message)));
  const totalPinned = pinnedMessages.length;
  const isCollapsible = totalPinned >= PINNED_STRIP_COLLAPSE_THRESHOLD;

  const [isExpanded, setIsExpanded] = useState(!isCollapsible);

  const onToggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const onItemClick = useCallback(id => () => onJumpToPinnedMessage(id), []);
  const onUnpinClick = useCallback(
    id => e => {
      e.stopPropagation();
      onUnpin(id);
    },
    [onUnpin],
  );

  if (!totalPinned) {
    return null;
  }

  const showItems = !isCollapsible || isExpanded;

  return (
    <Box sx={rootStyles}>
      <Box sx={headerStyles}>
        <Box sx={headerLabelStyles}>
          <PinFilledIcon size={IconSize[20]} />
          <Typography variant="medium12">
            {PINNED_STRIP_HEADER_LABEL} ({totalPinned})
          </Typography>
        </Box>
        {isCollapsible && (
          <Tooltip title={isExpanded ? PINNED_STRIP_COLLAPSE_TOOLTIP : PINNED_STRIP_EXPAND_TOOLTIP}>
            <IconButton
              aria-label={isExpanded ? PINNED_STRIP_COLLAPSE_TOOLTIP : PINNED_STRIP_EXPAND_TOOLTIP}
              data-testid={TeamChatPinnedStripTestIds.TOGGLE_BUTTON}
              size="small"
              onClick={onToggleExpanded}
            >
              <ArrowCheckIcon
                direction={isExpanded ? IconDirection.UP : IconDirection.DOWN}
                size={IconSize[18]}
              />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {showItems &&
        pinnedMessages.map(message => {
          const id = getMessageId(message);
          const senderLabel = getPinnedSenderLabel(message, members);
          const preview = buildPinnedPreview(getMessageText(message));

          return (
            <Box key={id} sx={itemStyles} onClick={onItemClick(id)}>
              <Box sx={itemBodyStyles}>
                <Typography sx={itemSenderStyles} variant="medium12">
                  {senderLabel}:
                </Typography>
                <Typography sx={itemPreviewStyles} variant="regular12">
                  {preview}
                </Typography>
              </Box>
              <Tooltip title={PINNED_STRIP_UNPIN_TOOLTIP}>
                <IconButton
                  aria-label={PINNED_STRIP_UNPIN_TOOLTIP}
                  data-testid={TeamChatPinnedStripTestIds.getUnpinButtonId(id)}
                  size="small"
                  onClick={onUnpinClick(id)}
                >
                  <PinFilledIcon size={IconSize[18]} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        })}
    </Box>
  );
});
TeamChatPinnedStrip.propTypes = {
  messages: PropTypes.arrayOf(PropTypes.object).isRequired,
  pinnedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onUnpin: PropTypes.func.isRequired,
  members: PropTypes.arrayOf(PropTypes.object),
};

export default TeamChatPinnedStrip;
